import type {
  AssistantReportDefinition,
  AssistantReportPreview,
  AssistantReportPreviewColumn,
  AssistantReportPreviewRow,
  IntellioAssistantDatasourceFamily,
  IntellioAssistantModule,
  IntellioAssistantResolvedDatasourcePlan,
} from '../entities/assistant';
import {
  AssistantReportDefinitionSchema,
  AssistantReportPreviewSchema,
  IntellioAssistantResolvedDatasourcePlanSchema,
} from '../entities/assistant';
import type { IntellioAssistantExecutionAdapter } from '../entities/assistant';
import {
  REPORT_GENERATION_ENTITIES_BY_ID,
  REPORT_GENERATION_TEMPLATES_BY_ID,
  type ReportGenerationCognitoProfile,
  type ReportGenerationShellPersona,
} from './reportGenerationSemanticCatalog';
import {
  resolveReportGenerationRequiredDatasourceFamilies,
  resolveReportGenerationReferencedEntityIds,
} from './reportGenerationDatasourcePlan';
import {
  buildReportGenerationFilterViewModel,
  deriveReportGenerationJoinIndicators,
  type ReportGenerationFilterState,
  type ReportGenerationFilterViewModel,
} from './reportGenerationFiltering';

export type RunReportsPrimitiveCell = string | number | boolean | null;

export type RunReportsEntityJoinKeys = {
  family_id?: string | null;
  student_id?: string | null;
  application_id?: string | null;
  applicant_id?: string | null;
  installment_id?: string | null;
  payment_id?: string | null;
  ledger_entry_id?: string | null;
  contract_id?: string | null;
};

export type RunReportsEntityRow = {
  id: string;
  entityId: string;
  joinKeys: RunReportsEntityJoinKeys;
  cells: Record<string, RunReportsPrimitiveCell>;
};

export type RunReportsSelectedFieldMetadata = {
  fieldId: string;
  fieldLabel: string;
  moduleLabel: string;
  entityId?: string | null;
  authority?: AssistantReportPreviewColumn['authority'];
};

export type RunReportsJoinedRowsResult = {
  rows: RunReportsEntityRow[];
  missingEntityIds: string[];
  unmatchedEntityIds: string[];
};

export type BuildRunReportsPreviewInput = {
  definition: AssistantReportDefinition;
  selectedFields: readonly RunReportsSelectedFieldMetadata[];
  rowsByEntity: ReadonlyMap<string, readonly RunReportsEntityRow[]>;
  datasourcePlan?: IntellioAssistantResolvedDatasourcePlan | null;
  joins?: readonly string[];
  blockedEntityIds?: readonly string[];
  blockedTemplateIds?: readonly string[];
  warnings?: readonly string[];
  limit?: number;
};

export const RUN_REPORTS_PREVIEW_DEFAULT_LIMIT = 30;
export const RUN_REPORTS_PREVIEW_QUERY_LIMIT = 500;
export const RUN_REPORTS_NON_PRODUCTION_ROW_WARNING =
  'Rows matching non-production/test marker denylist were excluded from the Run Reports preview.';
export const RUN_REPORTS_NON_PRODUCTION_ROW_DIAGNOSTIC_CODE =
  'run_reports_non_production_rows_suppressed';
export const RUN_REPORTS_NON_PRODUCTION_ROW_DIAGNOSTIC_MESSAGE =
  'Rows matching non-production/test markers were excluded from the Run Reports preview.';

export const RUN_REPORTS_LIVE_DATASOURCE_FAMILIES = [
  'studentsFamilies.records',
  'studentsFamilies.tuition',
] as const satisfies readonly IntellioAssistantDatasourceFamily[];

export const RUN_REPORTS_NON_PRODUCTION_ROW_PATTERNS = [
  /\bfake\b/i,
  /\bmock\b/i,
  /\bplaceholder\b/i,
  /\bseeded\b/i,
  /\bE2E\b/,
  /\bexample\.test\b/i,
  /\b555-\d{4}\b/,
  /\bfam-demo-[a-z0-9-]+\b/i,
  /\btuition-seeded-[a-z0-9-]+\b/i,
  /\btuition-adler-\d+\b/i,
  /\bE2E ledger entry for pagination check\b/i,
  /\bStudent-\d{8,}\b/i,
  /\bCodex Persist \d+\b/i,
] as const;

export type RunReportsEffectivePreviewRequest = {
  definition: AssistantReportDefinition;
  viewModel: ReportGenerationFilterViewModel;
  datasourcePlan: IntellioAssistantResolvedDatasourcePlan;
  authorityWarnings: string[];
  canRun: boolean;
};

export type BuildRunReportsEffectivePreviewRequestInput = {
  definition: AssistantReportDefinition;
  pathname: string;
  module: IntellioAssistantModule;
  profile: ReportGenerationCognitoProfile | null;
  effectivePersona: ReportGenerationShellPersona;
  executionAdapter?: IntellioAssistantExecutionAdapter;
  authorityScope?: 'tuition' | 'all';
  includeLegacy?: boolean;
  allowedDatasourceFamilies?: readonly IntellioAssistantDatasourceFamily[];
};

export type BuildConstrainedRunReportsDatasourcePlanInput = {
  definition: AssistantReportDefinition;
  module?: IntellioAssistantModule | null;
  executionAdapter?: IntellioAssistantExecutionAdapter;
  allowedDatasourceFamilies?: readonly IntellioAssistantDatasourceFamily[];
};

export const unwrapRunReportsAssistantPlatformInput = (
  input: Record<string, unknown>,
): Record<string, unknown> => {
  const nested = input.input;
  return nested && typeof nested === 'object' && !Array.isArray(nested)
    ? nested as Record<string, unknown>
    : input;
};

export const buildRunReportsFilterStateFromDefinition = (
  definition: AssistantReportDefinition,
): ReportGenerationFilterState => ({
  selectedEntities: [...definition.selectedEntities],
  selectedFields: [...definition.selectedFields],
  conditions: definition.conditions.map((condition) => ({ ...condition })),
  dateRange: {
    preset: definition.dateRange?.preset ?? 'Custom',
    from: definition.dateRange?.from ?? '',
    to: definition.dateRange?.to ?? '',
  },
  sorts: definition.sorts.map((sort) => ({ ...sort })),
});

export const normalizeRunReportsPreviewSourceInput = (
  input: Record<string, unknown>,
): Record<string, unknown> => {
  const unwrapped = unwrapRunReportsAssistantPlatformInput(input);
  const parsedDefinition = AssistantReportDefinitionSchema.safeParse(unwrapped.reportDefinition ?? {});
  if (!parsedDefinition.success) {
    return unwrapped;
  }
  const academicYear = resolveRunReportsAcademicYear(parsedDefinition.data);
  return {
    ...unwrapped,
    reportDefinition: {
      ...parsedDefinition.data,
      dateRange: {
        ...parsedDefinition.data.dateRange,
        preset: academicYear,
        from: null,
        to: null,
      },
    },
  };
};

const reportConditionKey = (condition: AssistantReportDefinition['conditions'][number]): string =>
  [
    condition.fieldId,
    condition.operator,
    String(condition.value ?? '').trim().toLowerCase(),
  ].join(':');

export const applyRunReportsTemplateIntrinsicConditions = (
  definition: AssistantReportDefinition,
): AssistantReportDefinition => {
  const template = definition.templateId
    ? REPORT_GENERATION_TEMPLATES_BY_ID[definition.templateId]
    : null;
  const templateConditions = template?.conditions ?? [];
  const templateSorts = template?.sorts ?? [];
  if (templateConditions.length === 0 && templateSorts.length === 0) {
    return definition;
  }
  const existingConditionKeys = new Set(definition.conditions.map(reportConditionKey));
  const conditions = [
    ...templateConditions.filter((condition) => !existingConditionKeys.has(reportConditionKey(condition))),
    ...definition.conditions,
  ];
  return AssistantReportDefinitionSchema.parse({
    ...definition,
    conditions,
    sorts: definition.sorts.length > 0
      ? definition.sorts
      : templateSorts.map((sort) => ({ ...sort })),
  });
};

const toPreviewColumnAuthority = (
  authority: AssistantReportPreviewColumn['authority'] | null | undefined,
): AssistantReportPreviewColumn['authority'] => {
  if (!authority) {
    return null;
  }
  return {
    scope: authority.scope,
    status: authority.status,
    source: authority.source ?? null,
    domainPackage: authority.domainPackage ?? null,
    domainType: authority.domainType ?? null,
    domainSchema: authority.domainSchema ?? null,
    operationId: authority.operationId,
    operationKind: authority.operationKind,
    appSyncRouter: authority.appSyncRouter,
    appSyncField: authority.appSyncField,
    resultPath: authority.resultPath,
    routeFamily: authority.routeFamily ?? null,
    datasourceFamily: authority.datasourceFamily ?? null,
    joinKey: authority.joinKey ?? null,
    permission: authority.permission ?? null,
  };
};

export const resolveRunReportsAcademicYear = (
  definition: Pick<AssistantReportDefinition, 'dateRange'>,
  now: Date = new Date(),
  timeZone?: string,
): string => {
  const explicit = String(definition.dateRange?.preset ?? '').trim();
  if (/^\d{4}-\d{4}$/.test(explicit)) {
    return explicit;
  }
  const fromYear = String(definition.dateRange?.from ?? '').trim().match(/^(\d{4})/)?.[1];
  if (fromYear) {
    const year = Number.parseInt(fromYear, 10);
    return `${year}-${year + 1}`;
  }
  let calendarYear = now.getFullYear();
  let calendarMonth = now.getMonth();
  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
    }).formatToParts(now);
    calendarYear = Number(parts.find((part) => part.type === 'year')?.value ?? calendarYear);
    calendarMonth = Number(parts.find((part) => part.type === 'month')?.value ?? calendarMonth + 1) - 1;
  }
  const schoolYearStart = calendarMonth < 6 ? calendarYear - 1 : calendarYear;
  return `${schoolYearStart}-${schoolYearStart + 1}`;
};

const GENERIC_RUN_REPORTS_PREVIEW_WARNING =
  "Some report information couldn't be included. Contact support if you need help with access.";

export const resolveUserFacingRunReportsPreviewWarning = (
  warning: string,
): string => {
  const normalized = String(warning ?? '').trim();
  if (
    /removed fields or entities outside the generated tuition report-field authority/i.test(
      normalized,
    )
  ) {
    return 'Some selected fields or data categories were removed because they are not available for tuition reports.';
  }
  if (normalized === 'no_authorized_datasource') {
    return 'This report uses school data you do not have access to.';
  }
  if (normalized === 'incomplete_report_datasource_scope') {
    return 'Some school data required by this report is unavailable with your current access.';
  }
  if (/outside the current actor scope/i.test(normalized)) {
    return 'This report includes school data you do not have access to.';
  }
  return GENERIC_RUN_REPORTS_PREVIEW_WARNING;
};

export const rowContainsRunReportsNonProductionMarker = (row: unknown): boolean => {
  const text = JSON.stringify(row ?? {});
  return RUN_REPORTS_NON_PRODUCTION_ROW_PATTERNS.some((pattern) => pattern.test(text));
};

const stripInternalRunReportsWarnings = (warnings: readonly string[] = []): string[] =>
  warnings.filter((warning) => warning !== RUN_REPORTS_NON_PRODUCTION_ROW_WARNING);

const withRunReportsNonProductionDiagnostic = (
  diagnostics: AssistantReportPreview['diagnostics'],
  suppressedRowCount: number,
): AssistantReportPreview['diagnostics'] => [
  ...diagnostics.filter((diagnostic) => diagnostic.code !== RUN_REPORTS_NON_PRODUCTION_ROW_DIAGNOSTIC_CODE),
  {
    code: RUN_REPORTS_NON_PRODUCTION_ROW_DIAGNOSTIC_CODE,
    severity: 'info',
    message: RUN_REPORTS_NON_PRODUCTION_ROW_DIAGNOSTIC_MESSAGE,
    rowCount: suppressedRowCount,
  },
];

export const stripRunReportsNonProductionPreviewRows = (
  preview: AssistantReportPreview,
): AssistantReportPreview => {
  const filteredRows = preview.rows.filter((row) => !rowContainsRunReportsNonProductionMarker(row));
  const warnings = stripInternalRunReportsWarnings(preview.warnings);
  if (filteredRows.length === preview.rows.length) {
    if (warnings.length === preview.warnings.length) {
      return preview;
    }
    return AssistantReportPreviewSchema.parse({
      ...preview,
      warnings,
    });
  }
  const suppressedRowCount = preview.rows.length - filteredRows.length;
  return AssistantReportPreviewSchema.parse({
    ...preview,
    rows: filteredRows,
    totalRowCount: Math.max(0, preview.totalRowCount - suppressedRowCount),
    canRun: preview.canRun && filteredRows.length > 0,
    warnings,
    diagnostics: withRunReportsNonProductionDiagnostic(preview.diagnostics, suppressedRowCount),
  });
};

export const stripRunReportsNonProductionPreviewPayload = (payload: unknown): unknown => {
  const parsedPreview = AssistantReportPreviewSchema.safeParse(payload);
  if (parsedPreview.success) {
    return stripRunReportsNonProductionPreviewRows(parsedPreview.data);
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload;
  }
  const preview = payload as Record<string, unknown>;
  if (!Array.isArray(preview.rows)) {
    return payload;
  }
  const rows = preview.rows.filter((row) => !rowContainsRunReportsNonProductionMarker(row));
  const warnings = Array.isArray(preview.warnings)
    ? preview.warnings.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const userWarnings = stripInternalRunReportsWarnings(warnings);
  if (rows.length === preview.rows.length) {
    return userWarnings.length === warnings.length
      ? payload
      : {
          ...preview,
          warnings: userWarnings,
        };
  }
  const suppressedRowCount = preview.rows.length - rows.length;
  const diagnostics = Array.isArray(preview.diagnostics)
    ? preview.diagnostics.filter((entry): entry is AssistantReportPreview['diagnostics'][number] =>
        Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry),
      )
    : [];
  return {
    ...preview,
    rows,
    totalRowCount: typeof preview.totalRowCount === 'number'
      ? Math.max(0, preview.totalRowCount - suppressedRowCount)
      : rows.length,
    canRun: typeof preview.canRun === 'boolean' ? preview.canRun && rows.length > 0 : preview.canRun,
    warnings: userWarnings,
    diagnostics: withRunReportsNonProductionDiagnostic(diagnostics, suppressedRowCount),
  };
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
  }
  return null;
};

const formatDate = (value: unknown): string => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) {
    return '';
  }
  if (/^\d+$/.test(normalized)) {
    const seconds = Number.parseInt(normalized, 10);
    const date = new Date(seconds > 10_000_000_000 ? seconds : seconds * 1000);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
    return normalized.slice(0, 10);
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? normalized : parsed.toISOString().slice(0, 10);
};

export const matchRunReportsCondition = (
  row: RunReportsEntityRow,
  condition: AssistantReportDefinition['conditions'][number],
): boolean => {
  const value = row.cells[condition.fieldId];
  const left = typeof value === 'string' ? value.toLowerCase() : value;
  const right = condition.value.toLowerCase();
  switch (condition.operator) {
    case 'equals':
    case 'is':
      return String(left ?? '').toLowerCase() === right;
    case 'contains':
      return String(left ?? '').toLowerCase().includes(right);
    case 'starts_with':
      return String(left ?? '').toLowerCase().startsWith(right);
    case 'gt':
      return (toNumber(value) ?? Number.NEGATIVE_INFINITY) > (toNumber(condition.value) ?? Number.POSITIVE_INFINITY);
    case 'lt':
      return (toNumber(value) ?? Number.POSITIVE_INFINITY) < (toNumber(condition.value) ?? Number.NEGATIVE_INFINITY);
    case 'between': {
      const [fromText, toTextValue] = String(condition.value ?? '').split('..');
      const numericValue = toNumber(value);
      if (numericValue == null) {
        return false;
      }
      const from = toNumber(fromText);
      const to = toNumber(toTextValue);
      if (from == null || to == null) {
        return false;
      }
      return numericValue >= from && numericValue <= to;
    }
    case 'before':
      return formatDate(value) < formatDate(condition.value);
    case 'after':
      return formatDate(value) > formatDate(condition.value);
    case 'is_true':
      return toBoolean(value) === true;
    case 'is_false':
      return toBoolean(value) === false;
    case 'is_not':
      return String(left ?? '').toLowerCase() !== right;
    default:
      return true;
  }
};

export const sortRunReportsRows = (
  rows: readonly RunReportsEntityRow[],
  sorts: AssistantReportDefinition['sorts'],
): RunReportsEntityRow[] => {
  const primary = sorts[0];
  if (!primary) {
    return [...rows];
  }
  return [...rows].sort((left, right) => {
    const a = left.cells[primary.fieldId];
    const b = right.cells[primary.fieldId];
    const comparison = String(a ?? '').localeCompare(String(b ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return primary.direction === 'asc' ? comparison : -comparison;
  });
};

export const sharedRunReportsJoinKey = (
  left: RunReportsEntityRow,
  right: RunReportsEntityRow,
): keyof RunReportsEntityJoinKeys | null => {
  const candidates: Array<keyof RunReportsEntityJoinKeys> = [
    'family_id',
    'student_id',
    'application_id',
    'applicant_id',
  ];
  for (const candidate of candidates) {
    const leftValue = left.joinKeys[candidate];
    const rightValue = right.joinKeys[candidate];
    if (leftValue && rightValue && leftValue === rightValue) {
      return candidate;
    }
  }
  return null;
};

export const joinRunReportsRowsStrictInner = (
  rowsByEntity: ReadonlyMap<string, readonly RunReportsEntityRow[]>,
  entityIds: readonly string[],
): RunReportsJoinedRowsResult => {
  const [baseEntityId, ...restEntityIds] = entityIds;
  if (!baseEntityId) {
    return {
      rows: [],
      missingEntityIds: [],
      unmatchedEntityIds: [],
    };
  }
  let rows = [...(rowsByEntity.get(baseEntityId) ?? [])];
  const missingEntityIds = rows.length === 0 ? [baseEntityId] : [];
  const unmatchedEntityIds: string[] = [];
  for (const entityId of restEntityIds) {
    const nextRows = rowsByEntity.get(entityId) ?? [];
    if (nextRows.length === 0) {
      missingEntityIds.push(entityId);
      rows = [];
      break;
    }
    const merged: RunReportsEntityRow[] = [];
    for (const row of rows) {
      const matching = nextRows.filter((candidate) => sharedRunReportsJoinKey(row, candidate) !== null);
      if (matching.length === 0) {
        continue;
      }
      for (const match of matching) {
        merged.push({
          id: `${row.id}::${match.id}`,
          entityId: row.entityId,
          joinKeys: {
            ...row.joinKeys,
            ...match.joinKeys,
          },
          cells: {
            ...row.cells,
            ...match.cells,
          },
        });
      }
    }
    if (merged.length === 0) {
      unmatchedEntityIds.push(entityId);
    }
    rows = merged;
  }
  return {
    rows,
    missingEntityIds,
    unmatchedEntityIds,
  };
};

export const buildRunReportsSourceEmptyWarnings = (
  entityIds: readonly string[],
  joinedRows: RunReportsJoinedRowsResult,
): string[] => (
  entityIds.length > 1
  && joinedRows.rows.length === 0
  && (joinedRows.missingEntityIds.length > 0 || joinedRows.unmatchedEntityIds.length > 0)
    ? [
        [
          'No source-backed rows matched the selected template/year.',
          joinedRows.missingEntityIds.length > 0
            ? `Missing rows for: ${joinedRows.missingEntityIds.join(', ')}.`
            : null,
          joinedRows.unmatchedEntityIds.length > 0
            ? `No shared source keys matched for: ${joinedRows.unmatchedEntityIds.join(', ')}.`
            : null,
        ].filter(Boolean).join(' '),
      ]
    : []
);

export const buildAssistantReportPreviewFromRunReportsRows = (
  input: BuildRunReportsPreviewInput,
): AssistantReportPreview => {
  const definition = applyRunReportsTemplateIntrinsicConditions(
    AssistantReportDefinitionSchema.parse(input.definition),
  );
  const joinEntityIds = resolveReportGenerationReferencedEntityIds(definition);
  const joinedRows = joinRunReportsRowsStrictInner(input.rowsByEntity, joinEntityIds);
  const filteredRows = joinedRows.rows.filter((row) =>
    definition.conditions.every((condition) => matchRunReportsCondition(row, condition)),
  );
  const sortedRows = sortRunReportsRows(filteredRows, definition.sorts);
  const limit = input.limit ?? RUN_REPORTS_PREVIEW_DEFAULT_LIMIT;
  const selectedFields = [...input.selectedFields];
  const previewRows: AssistantReportPreviewRow[] = sortedRows.slice(0, limit).map((row) => ({
    id: row.id,
    cells: Object.fromEntries(
      selectedFields.map((field) => [field.fieldId, row.cells[field.fieldId] ?? null]),
    ),
  }));

  return AssistantReportPreviewSchema.parse({
    definition,
    columns: selectedFields.map((field) => ({
      fieldId: field.fieldId,
      label: field.fieldLabel,
      moduleLabel: field.moduleLabel,
      entityId: field.entityId,
      authority: toPreviewColumnAuthority(field.authority),
    })),
    rows: previewRows,
    totalRowCount: sortedRows.length,
    joins: [...(input.joins ?? [])],
    blockedEntityIds: [...(input.blockedEntityIds ?? [])],
    blockedTemplateIds: [...(input.blockedTemplateIds ?? [])],
    canRun: sortedRows.length > 0 && selectedFields.length > 0,
    warnings: [
      ...(input.warnings ?? []),
      ...buildRunReportsSourceEmptyWarnings(joinEntityIds, joinedRows),
    ],
    datasourcePlan: input.datasourcePlan ?? null,
  });
};

type ReportFieldMetadata = {
  entityId: string;
  label: string;
  moduleLabel: string;
  authority: AssistantReportPreviewColumn['authority'];
};

const reportFieldMetadataById = (): Map<string, ReportFieldMetadata> => new Map(
  Object.values(REPORT_GENERATION_ENTITIES_BY_ID).flatMap((entity) =>
    entity.categories.flatMap((category) =>
      category.fields.map((field) => [field.id, {
        entityId: entity.id,
        label: field.label,
        moduleLabel: entity.moduleLabel,
        authority: field.authority ?? null,
      }] as const),
    ),
  ),
);

export const buildRunReportsPreviewColumns = (
  definition: AssistantReportDefinition,
): AssistantReportPreview['columns'] => {
  const metadataById = reportFieldMetadataById();
  const columns: AssistantReportPreview['columns'] = [];
  const selectedFields = [...new Set(definition.selectedFields ?? [])];
  for (const fieldId of selectedFields) {
    const metadata = metadataById.get(fieldId);
    if (!metadata) {
      continue;
    }
    columns.push({
      fieldId,
      label: metadata.label,
      moduleLabel: metadata.moduleLabel,
      entityId: metadata.entityId,
      authority: toPreviewColumnAuthority(metadata.authority),
    });
  }
  return columns;
};

export const buildRunReportsDatasourcePlan = (
  definition: AssistantReportDefinition,
  executionAdapter: IntellioAssistantExecutionAdapter = 'domain-router-query',
): IntellioAssistantResolvedDatasourcePlan => {
  const requiredDatasourceFamilies = resolveReportGenerationRequiredDatasourceFamilies(definition);
  const executionPlan = requiredDatasourceFamilies.map((datasourceFamily) => ({
    datasourceFamily,
    executionAdapter,
  }));
  return IntellioAssistantResolvedDatasourcePlanSchema.parse({
    capabilityId: null,
    module: definition.module ?? 'tuition',
    routeFamily: definition.routeFamily ?? 'tuition',
    requiredDatasourceFamilies,
    selectedDatasourceFamilies: requiredDatasourceFamilies,
    executionPlan,
    allowed: true,
    denialReason: null,
  });
};

export const buildConstrainedRunReportsDatasourcePlan = ({
  definition,
  module,
  executionAdapter = 'domain-router-query',
  allowedDatasourceFamilies = RUN_REPORTS_LIVE_DATASOURCE_FAMILIES,
}: BuildConstrainedRunReportsDatasourcePlanInput): IntellioAssistantResolvedDatasourcePlan => {
  const requiredDatasourceFamilies = resolveReportGenerationRequiredDatasourceFamilies(definition);
  const allowedFamilies = new Set<IntellioAssistantDatasourceFamily>(allowedDatasourceFamilies);
  const selectedDatasourceFamilies = requiredDatasourceFamilies.filter((family) => allowedFamilies.has(family));
  const allRequiredDatasourceFamiliesSelected = requiredDatasourceFamilies.length > 0
    && selectedDatasourceFamilies.length === requiredDatasourceFamilies.length;

  return IntellioAssistantResolvedDatasourcePlanSchema.parse({
    capabilityId: null,
    module: module ?? definition.module ?? 'tuition',
    routeFamily: definition.routeFamily ?? 'tuition',
    requiredDatasourceFamilies,
    selectedDatasourceFamilies,
    executionPlan: selectedDatasourceFamilies.map((datasourceFamily) => ({
      datasourceFamily,
      executionAdapter,
    })),
    allowed: allRequiredDatasourceFamiliesSelected,
    denialReason: allRequiredDatasourceFamiliesSelected
      ? null
      : selectedDatasourceFamilies.length === 0
        ? 'no_authorized_datasource'
        : 'incomplete_report_datasource_scope',
  });
};

const sameStringList = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const buildRunReportsEffectivePreviewRequest = ({
  definition,
  pathname,
  module,
  profile,
  effectivePersona,
  executionAdapter = 'domain-router-query',
  authorityScope = pathname === '/school/run-reports' ? 'tuition' : 'all',
  includeLegacy = pathname !== '/school/run-reports',
  allowedDatasourceFamilies = RUN_REPORTS_LIVE_DATASOURCE_FAMILIES,
}: BuildRunReportsEffectivePreviewRequestInput): RunReportsEffectivePreviewRequest => {
  const parsedDefinition = AssistantReportDefinitionSchema.parse(definition);
  const viewModel = buildReportGenerationFilterViewModel({
    profile,
    effectivePersona,
    state: buildRunReportsFilterStateFromDefinition(parsedDefinition),
    entitySearch: '',
    selectedTemplateId: parsedDefinition.templateId ?? null,
    authorityScope,
    includeLegacy,
  });
  const effectiveDefinition = AssistantReportDefinitionSchema.parse({
    ...parsedDefinition,
    pathname,
    module,
    selectedEntities: [...viewModel.state.selectedEntities],
    selectedFields: [...viewModel.state.selectedFields],
    conditions: viewModel.state.conditions.map((condition) => ({ ...condition })),
    sorts: viewModel.state.sorts.map((sort) => ({ ...sort })),
  });
  const selectionWasConstrained =
    !sameStringList(effectiveDefinition.selectedEntities, parsedDefinition.selectedEntities)
    || !sameStringList(effectiveDefinition.selectedFields, parsedDefinition.selectedFields)
    || effectiveDefinition.conditions.length !== parsedDefinition.conditions.length
    || effectiveDefinition.sorts.length !== parsedDefinition.sorts.length;
  const authorityWarnings = authorityScope !== 'all' && selectionWasConstrained
    ? ['Run Reports preview removed fields or entities outside the generated tuition report-field authority.']
    : [];
  const datasourcePlan = buildConstrainedRunReportsDatasourcePlan({
    definition: effectiveDefinition,
    module,
    executionAdapter,
    allowedDatasourceFamilies,
  });

  return {
    definition: effectiveDefinition,
    viewModel,
    datasourcePlan,
    authorityWarnings,
    canRun: datasourcePlan.allowed && viewModel.canRunReport,
  };
};

export const buildRunReportsJoinIndicators = (
  definition: Pick<AssistantReportDefinition, 'selectedEntities'>,
): string[] => deriveReportGenerationJoinIndicators(definition.selectedEntities ?? []);

export const buildLocalReportPreviewColumns = buildRunReportsPreviewColumns;
export const buildLocalReportDatasourcePlan = (
  definition: AssistantReportDefinition,
): IntellioAssistantResolvedDatasourcePlan => buildRunReportsDatasourcePlan(definition, 'domain-router-query');
export const buildLocalReportJoinIndicators = buildRunReportsJoinIndicators;
